// @ts-nocheck
/**
 * Schema Inference Service
 * 
 * Infers property schemas from node definitions at runtime
 */

import type { NodeDefinition } from '@shared/types/node-definition';
import type { NodePropertySchema, NodePropertyDefinition } from '@shared/types/properties';

// Type alias for convenience
type PropertySchema = NodePropertyDefinition;
import type {
  InferredNodeSchema,
  InferredPropertySchema,
  SchemaInferenceResult,
} from '@/types/inferred-schema';
import { schemaCache } from './schema-cache';
import {
  getNodeDefinition,
  getNodeSchema,
  getNodeDefaults,
} from './node-registry';

/**
 * Infer schema from node definition
 */
export function inferSchemaFromDefinition(
  nodeType: string
): SchemaInferenceResult {
  try {
    // Check cache first
    const cached = schemaCache.get(nodeType);
    if (cached) {
      return { success: true, schema: cached };
    }

    // Get node definition
    const definition = getNodeDefinition(nodeType);
    if (!definition) {
      return {
        success: false,
        error: `Node definition not found for type: ${nodeType}`,
      };
    }

    // Get property schema
    const propertySchema = getNodeSchema(nodeType);
    if (!propertySchema) {
      return {
        success: false,
        error: `Property schema not found for type: ${nodeType}`,
      };
    }

    // Get defaults
    const defaults = getNodeDefaults(nodeType);

    // Build inferred schema
    const inferredSchema: InferredNodeSchema = {
      nodeType,
      displayName: definition.name,
      description: definition.description,
      category: definition.category,
      properties: inferPropertiesFromSchema(propertySchema, defaults),
      inputs: inferInputsFromDefinition(definition),
      outputs: inferOutputsFromDefinition(definition),
    };

    // Cache the result
    schemaCache.set(nodeType, inferredSchema);

    return { success: true, schema: inferredSchema };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Infer properties from property schema
 */
function inferPropertiesFromSchema(
  schema: PropertySchema[] | Record<string, PropertySchema>,
  defaults: Record<string, any>
): InferredPropertySchema[] {
  // Handle array format (NodePropertyDefinition[])
  if (Array.isArray(schema)) {
    return schema.map((propSchema) => {
      // Support both 'key' and 'name' for property identifier
      const propertyKey = propSchema.key || (propSchema as any).name;
      
      // Map 'options' type to 'select' for the UI
      const uiType = propSchema.type === 'options' ? 'select' : propSchema.type;
      
      // Map 'options' array to 'values' array with correct format for select fields
      const selectValues = propSchema.options?.map((opt: any) => ({
        value: opt.value,
        label: opt.name || opt.label,
      }));
      
      // For collection types, preserve the 'values' property as-is (it contains sub-field definitions)
      const collectionValues = (propSchema as any).values;
      
      const section = (propSchema as any).section || 'properties';
      
      // Support both 'label' and 'displayName' for property label
      const propertyLabel = propSchema.label || (propSchema as any).displayName;
      
      return {
        key: propertyKey,
        type: uiType,
        label: propertyLabel || (propertyKey ? formatLabel(propertyKey) : 'Unknown'),
        description: propSchema.description,
        required: propSchema.required ?? false,
        readOnly: (propSchema as any).readOnly,
        default: propSchema.default ?? (propertyKey ? defaults[propertyKey] : undefined),
        validation: propSchema.validation,
        options: propSchema.options,
        values: collectionValues || selectValues,
        placeholder: propSchema.placeholder,
        helpText: propSchema.helpText,
        displayOptions: propSchema.displayOptions,
        credentialTypes: (propSchema as any).credentialTypes,
        typeOptions: (propSchema as any).typeOptions,
        customComponent: (propSchema as any).customComponent,
        fixedCollectionOptions: (propSchema as any).fixedCollectionOptions,
        section: section,
        optionsSource: (propSchema as any).optionsSource,
        fromAI: (propSchema as any).fromAI,
        modeToggle: (propSchema as any).modeToggle,
        updatesField: (propSchema as any).updatesField,
      };
    });
  }

  // Handle record format
  return Object.entries(schema).map(([key, propSchema]) => {
    // Map 'options' type to 'select' for the UI
    const uiType = propSchema.type === 'options' ? 'select' : propSchema.type;
    
    // Map 'options' array to 'values' array with correct format for select fields
    const selectValues = propSchema.options?.map((opt: any) => ({
      value: opt.value,
      label: opt.name || opt.label,
    }));
    
    // For collection types, preserve the 'values' property as-is (it contains sub-field definitions)
    const collectionValues = (propSchema as any).values;
    
    return {
      key,
      type: uiType,
      label: propSchema.label || formatLabel(key),
      description: propSchema.description,
      required: propSchema.required ?? false,
      readOnly: (propSchema as any).readOnly,
      default: propSchema.default ?? defaults[key],
      validation: propSchema.validation,
      options: propSchema.options,
      values: collectionValues || selectValues,
      placeholder: propSchema.placeholder,
      helpText: propSchema.helpText,
      displayOptions: propSchema.displayOptions,
      credentialTypes: (propSchema as any).credentialTypes,
      typeOptions: (propSchema as any).typeOptions,
      customComponent: (propSchema as any).customComponent,
      fixedCollectionOptions: (propSchema as any).fixedCollectionOptions,
      section: (propSchema as any).section || 'properties',
      optionsSource: (propSchema as any).optionsSource,
      fromAI: (propSchema as any).fromAI,
      modeToggle: (propSchema as any).modeToggle,
      updatesField: (propSchema as any).updatesField,
    };
  });
}

/**
 * Infer inputs from node definition
 */
function inferInputsFromDefinition(definition: NodeDefinition) {
  // For now, return empty array
  // This will be enhanced in Phase 3 with actual input/output definitions
  return [];
}

/**
 * Infer outputs from node definition
 */
function inferOutputsFromDefinition(definition: NodeDefinition) {
  // For now, return empty array
  // This will be enhanced in Phase 3 with actual input/output definitions
  return [];
}

/**
 * Format property key to human-readable label
 */
function formatLabel(key: string): string {
  if (!key || typeof key !== 'string') {
    return 'Unknown';
  }
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
}

/**
 * Get property field type from schema type
 */
export function getPropertyFieldType(
  schema: PropertySchema
): InferredPropertySchema['type'] {
  return schema.type;
}

/**
 * Check if property is required
 */
export function isPropertyRequired(
  nodeType: string,
  propertyKey: string
): boolean {
  const result = inferSchemaFromDefinition(nodeType);
  if (!result.success || !result.schema) return false;

  const property = result.schema.properties.find((p) => p.key === propertyKey);
  return property?.required ?? false;
}

/**
 * Get property default value
 */
export function getPropertyDefault(
  nodeType: string,
  propertyKey: string
): any {
  const result = inferSchemaFromDefinition(nodeType);
  if (!result.success || !result.schema) return undefined;

  const property = result.schema.properties.find((p) => p.key === propertyKey);
  return property?.default;
}

/**
 * Get all properties for a node type
 */
export function getNodeProperties(
  nodeType: string
): InferredPropertySchema[] {
  const result = inferSchemaFromDefinition(nodeType);
  if (!result.success || !result.schema) return [];

  return result.schema.properties;
}

/**
 * Initialize schema cache for all nodes
 */
export function initializeSchemaCache(): void {
  // This will be called on app startup to pre-cache all schemas
  // For now, schemas are cached on-demand
  schemaCache.markInitialized();
}

/**
 * Clear schema cache
 */
export function clearSchemaCache(): void {
  schemaCache.clear();
}

/**
 * Get schema cache statistics
 */
export function getSchemaStats() {
  return schemaCache.getStats();
}
